import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { useSendCCAInviteMutation } from '../../../../../utils/api_service/ccaApi';
import { CCAQuestionResult, CCAStatus } from '../../../../../utils/types/cca_types';
import { Applicant } from '../../../../../utils/types/JobOpenings';
import { CCA_SUBDOMAINS } from '../../../../questions/CCABank/ccaConstants';
import { useCCAApplicantResult } from '../Hooks/UseCCAApplicantResult';

const toDisplayName = (s: string) =>
  s.includes('_')
    ? s
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
    : s;

const scoreColor = (score: number) => {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-rose-600';
};

type StatusKey = 'completed' | 'in-progress' | 'invite-sent' | 'other';

const mapStatusKey = (status: CCAStatus): StatusKey => {
  if (status === 'Completed') return 'completed';
  if (status === 'In Progress') return 'in-progress';
  if (status === 'Invite Sent') return 'invite-sent';
  return 'other';
};

const statusConfig: Record<
  StatusKey,
  { label: string; cls: string; icon: React.ReactNode }
> = {
  'invite-sent': {
    label: 'Invite Sent',
    cls: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: (
      <svg
          className='w-4 h-4'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
      >
        <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
        />
      </svg>
    ),
  },
  'in-progress': {
    label: 'In Progress',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: (
      <svg
          className='w-4 h-4 animate-spin'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
      >
        <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
        />
      </svg>
    ),
  },
  completed: {
    label: 'Completed',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: (
      <svg
          className='w-4 h-4'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
      >
        <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
        />
      </svg>
    ),
  },
  other: {
    label: 'Not Invited',
    cls: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: (
      <svg
          className='w-4 h-4'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
      >
        <circle cx='12' cy='12' r='10' />
        <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M12 8v4m0 4h.01'
        />
      </svg>
    ),
  },
};

// â”€â”€â”€ Subdomain colours â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const subdomainColors: Record<string, { bar: string; badge: string }> = {
  'Abstract Reasoning': {
    bar: 'bg-violet-500',
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  'Logical Reasoning': {
    bar: 'bg-blue-500',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  'Quantitative Aptitude': {
    bar: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  'Verbal Ability': {
    bar: 'bg-amber-500',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  'Data Interpretation': {
    bar: 'bg-rose-500',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
  },
};

const getSubdomainColors = (subdomain: string) =>
  subdomainColors[toDisplayName(subdomain)] ?? {
    bar: 'bg-slate-400',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
  };

// â”€â”€â”€ Props â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface CCAResultsTabProps {
  candidate: Applicant;
}

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export const CCAResultsTab: React.FC<CCAResultsTabProps> = ({ candidate }) => {
  const { id: jobOpeningId = '' } = useParams<{ id: string }>();
  const { result, subdomainScores, isLoading, isError } = useCCAApplicantResult(
    candidate.userId,
  );

  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [sendInviteMutation, { isLoading: isSending }] = useSendCCAInviteMutation();

  const handleSendInvite = async () => {
    if (!jobOpeningId || !candidate.userId) return;

    try {
      await sendInviteMutation({ jobOpeningId, applicantIds: [candidate.userId] }).unwrap();
      toast.success('CCA invite sent successfully.', {
        position: 'bottom-center',
      });
    } catch {
      toast.error('Failed to send CCA invite. Please try again.', {
        position: 'bottom-center',
      });
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='h-28 w-full animate-pulse rounded-2xl bg-slate-100' />
        <div className='space-y-2'>
          { Array.from({ length: 5 }).map((_, i) => (
            <div
                key={i}
                className='h-16 w-full animate-pulse rounded-xl bg-slate-100'
            />
          )) }
        </div>
      </div>
    );
  }

  if (isError || !result) {
    return (
      <div className='p-12 text-center bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-200 m-6'>
        <div className='w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100'>
          <svg
              className='w-10 h-10 text-gray-300'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-bold text-gray-900 mb-2 tracking-tight'>
          No CCA data available
        </h3>
        <p className='text-gray-500 text-sm max-w-[260px] mx-auto mb-8'>
          { isError
            ? 'Failed to load result. You can send a CCA invite to start the assessment.'
            : 'This candidate has not been invited to the CCA yet.' }
        </p>

        <button
            onClick={handleSendInvite}
            disabled={isSending || !jobOpeningId}
            className='group bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-emerald-400 disabled:to-teal-400 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 mx-auto ring-4 ring-emerald-50'
            aria-label='Send CCA invite'
        >
          { isSending ? (
            <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
          ) : (
            <svg
                className='w-4 h-4 transition-transform duration-200 group-hover:scale-110'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
          ) }
          <span className='tracking-tight'>
            { isSending ? 'Sending Invitation...' : 'Send CCA Invite' }
          </span>
        </button>
      </div>
    );
  }

  const statusKey = mapStatusKey(result.assessment.status);
  const sc = statusConfig[statusKey];
  const isCompleted = statusKey === 'completed';

  const subdomainOrder: string[] = (CCA_SUBDOMAINS as string[]).length
    ? (CCA_SUBDOMAINS as string[])
    : Object.keys(subdomainScores);
  const hasResultData =
    Object.keys(subdomainScores).length > 0 ||
    (result.responses && result.responses.length > 0) ||
    result.assessment.totalScore != null;

  return (
    <div className='space-y-6'>
      { /* Header card */ }
      <div className='rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm'>
        <div className='flex items-center justify-between flex-wrap gap-3'>
          <div>
            <h3 className='text-base font-bold text-slate-800'>
              Cognitive Capability Assessment
            </h3>
            <p className='text-xs text-slate-500 mt-0.5'>
              5 Subdomains Â· 10 Levels Â· Video-enabled
            </p>
          </div>
          <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${sc.cls}`}
          >
            { sc.icon }
            { sc.label }
          </span>
        </div>

        { isCompleted && result.assessment.totalScore != null && (
          <div className='mt-4 flex items-center gap-4'>
            <div className='text-center'>
              <p className='text-3xl font-black text-slate-800'>
                <span className={scoreColor(result.assessment.totalScore)}>
                  { result.assessment.totalScore }
                </span>
                <span className='text-lg font-semibold text-slate-400'>
                  /100
                </span>
              </p>
              <p className='text-[11px] text-slate-500 mt-0.5'>Overall Score</p>
            </div>
            { result.assessment.completedAt && (
              <div className='flex-1 text-right'>
                <p className='text-xs text-slate-500'>Completed</p>
                <p className='text-sm font-semibold text-slate-700'>
                  { new Date(result.assessment.completedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  }) }
                </p>
              </div>
            ) }
          </div>
        ) }

        { statusKey === 'invite-sent' && (
          <p className='mt-3 text-sm text-slate-500'>
            The assessment invite has been sent. Awaiting the candidate to
            begin.
          </p>
        ) }
        { statusKey === 'in-progress' && (
          <p className='mt-3 text-sm text-amber-600'>
            The candidate has started the assessment. Results will appear once
            completed.
          </p>
        ) }
        { statusKey === 'other' && (
          <p className='mt-3 text-sm text-slate-500'>
            This candidate has not yet been invited to take the CCA.
          </p>
        ) }
      </div>

      { isCompleted && !hasResultData && (
        <div className='p-12 text-center bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-200'>
          <div className='w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm border border-gray-100'>
            <svg
                className='w-8 h-8 text-gray-300'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z'
              />
            </svg>
          </div>
          <h3 className='text-lg font-bold text-gray-900 mb-2 tracking-tight'>
            No CCA results available
          </h3>
          <p className='text-gray-500 text-sm max-w-[300px] mx-auto'>
            The CCA assessment record exists, but no score or question results
            are available for this candidate yet.
          </p>
        </div>
      ) }

      { /* Subdomain scores */ }
      { isCompleted && Object.keys(subdomainScores).length > 0 && (
        <div>
          <h4 className='text-xs font-bold uppercase tracking-wide text-slate-500 mb-3'>
            Subdomain Breakdown
          </h4>
          <div className='space-y-3'>
            { subdomainOrder.map((sd) => {
              const score =
                subdomainScores[sd] ??
                subdomainScores[sd.toLowerCase().replace(/ /g, '_')] ??
                0;
              const colors = getSubdomainColors(sd);
              return (
                <div
                    key={sd}
                    className='rounded-xl border border-slate-100 bg-white p-4 shadow-sm'
                >
                  <div className='flex items-center justify-between mb-2'>
                    <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${colors.badge}`}
                    >
                      { toDisplayName(sd) }
                    </span>
                    <span className={`text-sm font-bold ${scoreColor(score)}`}>
                      { score }%
                    </span>
                  </div>
                  <div className='h-2 rounded-full bg-slate-100'>
                    <div
                        className={`h-2 rounded-full transition-all duration-500 ${colors.bar}`}
                        style={{ width: `${score}%` }}
                    />
                  </div>
                </div>
              );
            }) }
          </div>
        </div>
      ) }

      { /* Per-question results */ }
      { isCompleted &&
        result.responses &&
        result.responses.length > 0 && (
        <div>
          <h4 className='text-xs font-bold uppercase tracking-wide text-slate-500 mb-3'>
            Question Results ({ result.responses.length })
          </h4>
          <div className='space-y-2'>
            { result.responses.map((qr: CCAQuestionResult, i: number) => {
              const isExpanded = expandedQuestion === qr.questionId;
              const correct =
                  qr.isCorrect ?? (qr.aiScore != null ? qr.aiScore >= 80 : false);
              const questionText = qr.questionSnapshot.question.text ?? '(image question)';
              const candidateAnswer =
                  qr.shortAnswerText ??
                  (qr.questionSnapshot.options.find(o => o.id === qr.selectedOptionId)?.text ?? null);
              return (
                <div
                    key={qr.questionId}
                    className='rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden'
                >
                  <button
                      type='button'
                      className='w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors'
                      onClick={() =>
                        setExpandedQuestion(isExpanded ? null : qr.questionId)
                      }
                  >
                    <span
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          correct
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-rose-100 text-rose-700'
                        }`}
                    >
                      { i + 1 }
                    </span>
                    <p className='flex-1 text-sm text-slate-700 line-clamp-2'>
                      { questionText }
                    </p>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                      { qr.aiScore != null && (
                        <span
                            className={`text-xs font-bold ${scoreColor(
                              qr.aiScore,
                            )}`}
                        >
                          { qr.aiScore }%
                        </span>
                      ) }
                      <svg
                          className={`w-4 h-4 text-slate-400 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth={2}
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M19 9l-7 7-7-7'
                        />
                      </svg>
                    </div>
                  </button>

                  { isExpanded && (
                    <div className='px-4 pb-4 pt-1 border-t border-slate-50 space-y-3'>
                      { candidateAnswer && (
                        <div>
                          <p className='text-[11px] font-semibold text-slate-500 mb-1'>
                            Candidate Answer
                          </p>
                          <p
                              className={`text-sm font-medium px-3 py-2 rounded-lg ${
                                correct
                                  ? 'bg-emerald-50 text-emerald-800'
                                  : 'bg-rose-50 text-rose-800'
                              }`}
                          >
                            { candidateAnswer }
                          </p>
                        </div>
                      ) }
                      <div className='flex items-center gap-1.5 flex-wrap'>
                        <span className='text-[11px] text-slate-400'>
                          { toDisplayName(qr.subdomain) }
                        </span>
                        <span className='text-[11px] text-slate-300'>Â·</span>
                        <span className='text-[11px] text-slate-400'>
                          Level { qr.questionSnapshot.level }
                        </span>
                        <span className='text-[11px] text-slate-300'>Â·</span>
                        <span className='text-[11px] text-slate-400 capitalize'>
                          { qr.questionType.replace('_', ' ') }
                        </span>
                      </div>
                    </div>
                  ) }
                </div>
              );
            }) }
          </div>
        </div>
      ) }
    </div>
  );
};
